/**
 * useMonthlyReport — W2.2 / Contract MR-v1
 *
 * Ruft GET /api/v2/monthly_reports?month=&year= vom Backend ab.
 * `time_zone` wird bewusst NICHT mitgeschickt (der Endpoint wertet es nicht aus,
 * siehe R3-Review F3) — die Zeitzone gilt nur fuer CSV-Export und Mailversand.
 * Liefert report (gemappt auf das MR-v1-Schema), isLoading, isError, error.
 *
 * Bei HTTP 402: isPremiumRequired=true (kein echter Fehler, nur Guard).
 * Bei HTTP 422: isInvalidPeriod=true.
 *
 * Der Mapper mapMonthlyReport unwrappt data.summary + data.hotspots + data.daily
 * und liest robust (gross_rewards_hnt ?? total_rewards_hnt). Top-Level-Token-Totals
 * (total_hnt/iot/mobile) werden aus der Summary abgeleitet (Abwaerts-Kompatibilitaet
 * fuer ReportHero / Empty-State-Logik).
 */
import { useQuery } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'
import { ApiError } from '../../lib/errors/ApiError'

// --- Public Types (MR-v1) ---

export type MonthlySummary = {
  gross_rewards_hnt: number
  commissions_paid_hnt: number
  net_rewards_hnt: number
  rewards_iot: number
  rewards_mobile: number
  /** @deprecated Alias fuer avg_hnt_fiat_rate bei EUR-Konten/alten BE-Staenden. */
  avg_hnt_eur_rate: number
  /** @deprecated Alias fuer total_rewards_fiat bei EUR-Konten/alten BE-Staenden. */
  total_rewards_eur: number
  /**
   * BE #19: Kontowaehrung (ISO 4217, z.B. "USD", "GBP", "CAD", "TRY").
   * Aeltere BE-Staende liefern das Feld noch nicht — Fallback ist "EUR"
   * (bisheriges, fest verdrahtetes Verhalten).
   */
  currency: string
  /** BE #19: Durchschnittskurs HNT -> Kontowaehrung. Alias von avg_hnt_eur_rate. */
  avg_hnt_fiat_rate: number
  /** BE #19: Gesamt-Rewards in Kontowaehrung. Alias von total_rewards_eur. */
  total_rewards_fiat: number
  tx_count: number
  /** Gesamtzahl der Hotspots im Report (optional: aeltere BE-Stände liefern es nicht) */
  hotspots_count?: number
  /** R3/BE-8: nur Hotspots MIT Rewards — v1-Kopfzeile "N Hotspots generated ..." */
  active_hotspots?: number
}

/**
 * Provisions-Aufschluesselung pro Hotspot.
 *
 * Issue #53: Quelle sind seit api-types 0.4.10 die v2-`host_split_rules` —
 * dieselbe Quelle wie /commissions, Settlements und dashboard.commissions_mtd.
 * Die Legacy-Tabelle `shares` wird vom Report nicht mehr gelesen.
 * `contact_id`/`contact_name` tragen host_id/host.name.
 */
export type HotspotShare = {
  id: number
  contact_id: number | null
  contact_name: string
  /** Prozentsatz; 0 bei Fixbetrag-Regeln. */
  percentage: number
  split_type?: 'percentage' | 'fixed_amount'
  /** Fixbetrag in `payout_currency`; null bei Prozent-Regeln. */
  fixed_amount?: number | null
  payout_currency?: string
  start_date?: string
  end_date?: string
  shared_hnt: number
  shared_iot: number
  shared_mobile: number
}

export type HotspotReward = {
  id: string
  name: string
  /** Blockchain-Adresse des Hotspots — FE-Fallback-Anzeigename, falls `name`
   * eine durchgereichte Lookup-Fehlermeldung ist (z.B. "Error: No blockchain
   * asset found") statt eines echten Hotspot-Namens. */
  address?: string
  /** R3/BE-7: physische Adresse (NICHT die Blockchain-Adresse) */
  street?: string
  city?: string
  /** R3/BE-7: Besitz-/Report-Zeitraum innerhalb der Periode (ISO-Datum) */
  period_start?: string
  period_end?: string
  rewards_hnt: number
  rewards_iot: number
  rewards_mobile: number
  /** @deprecated Alias fuer rewards_fiat bei EUR-Konten/alten BE-Staenden. */
  rewards_eur: number
  /** BE #19: Rewards in Kontowaehrung (summary.currency). Alias von rewards_eur. */
  rewards_fiat: number
  /** R3/BE-7 — optional, damit aeltere Fixtures/BE-Staende weiter typen. */
  shares?: HotspotShare[]
}

export type DailyReward = {
  date: string
  rewards_hnt: number
}

export type MonthlyReport = {
  /** Periode als YYYY-MM (abgeleitet aus data.period.year/month) */
  period: string
  /** Lesbares Label, z.B. "Mai 2026" */
  period_label: string
  /** Top-Level-Token-Totals (aus Summary abgeleitet, Abwaerts-Kompat) */
  total_hnt: number
  total_iot: number
  total_mobile: number
  summary: MonthlySummary
  hotspots: HotspotReward[]
  daily: DailyReward[]
}

// --- Robuste Lese-Helfer (kein `as any`) ---

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null
}

function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback
}

/** gross_rewards_hnt mit total_rewards_hnt als Alias (BE liefert evtl. beides) */
function readGross(summary: Record<string, unknown>): number {
  if (typeof summary.gross_rewards_hnt === 'number') return summary.gross_rewards_hnt
  if (typeof summary.total_rewards_hnt === 'number') return summary.total_rewards_hnt
  return 0
}

/** ISO-4217-Code: exakt 3 Buchstaben (alles andere ist ein kaputter/leerer Wert). */
const ISO_4217_RE = /^[A-Za-z]{3}$/

/**
 * BE #19: Kontowaehrung + Fiat-Kurs/-Betrag mit Rueckfall auf die alten
 * *_eur-Felder (Alias, dieselben Werte) fuer BE-Staende ohne die neuen
 * Felder — 95% der Nutzer haben currency != EUR, daher DARF hier nicht
 * hart "EUR" angenommen werden, sobald das BE die neuen Felder liefert.
 * Ein nicht-ISO-4217-Code (z.B. leer/kaputt) faellt ebenfalls auf "EUR"
 * zurueck, statt als Label durchgereicht zu werden — formatCurrency() wuerde
 * bei einem ungueltigen Code sonst eine RangeError werfen.
 */
function readCurrency(s: Record<string, unknown>): string {
  return typeof s.currency === 'string' && ISO_4217_RE.test(s.currency) ? s.currency : 'EUR'
}

function readAvgFiatRate(s: Record<string, unknown>): number {
  if (typeof s.avg_hnt_fiat_rate === 'number') return s.avg_hnt_fiat_rate
  return num(s.avg_hnt_eur_rate)
}

function readTotalFiat(s: Record<string, unknown>): number {
  if (typeof s.total_rewards_fiat === 'number') return s.total_rewards_fiat
  return num(s.total_rewards_eur)
}

function mapSummary(raw: unknown): MonthlySummary {
  const s = isRecord(raw) ? raw : {}
  const avgFiatRate = readAvgFiatRate(s)
  const totalFiat = readTotalFiat(s)
  return {
    gross_rewards_hnt: readGross(s),
    commissions_paid_hnt: num(s.commissions_paid_hnt),
    net_rewards_hnt: num(s.net_rewards_hnt),
    rewards_iot: num(s.rewards_iot),
    rewards_mobile: num(s.rewards_mobile),
    // Alias-Felder bleiben erhalten (Abwaerts-Kompat fuer evtl. weitere Leser).
    avg_hnt_eur_rate: num(s.avg_hnt_eur_rate) || avgFiatRate,
    total_rewards_eur: num(s.total_rewards_eur) || totalFiat,
    currency: readCurrency(s),
    avg_hnt_fiat_rate: avgFiatRate,
    total_rewards_fiat: totalFiat,
    tx_count: num(s.tx_count),
    // R3-Review F7: optionale Felder NUR setzen, wenn sie im Rohobjekt stehen.
    // Vorher wurde ein fehlendes active_hotspots zu 0 normalisiert — dadurch war
    // im UI nicht mehr unterscheidbar, ob das BE das Feld nicht liefert oder
    // legitim 0 aktive Hotspots meldet (`??`-Fallback lief ins Leere).
    ...(typeof s.hotspots_count === 'number' ? { hotspots_count: num(s.hotspots_count) } : {}),
    ...(typeof s.active_hotspots === 'number' ? { active_hotspots: num(s.active_hotspots) } : {}),
  }
}

function optStr(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined
}

/**
 * R3-Review P0 (Design-Audit 2026-09-01): das BE liefert pro Hotspot teils
 * dutzende/hunderte Share-Zeilen fuer denselben Kontakt + denselben
 * Prozentsatz statt einer Zeile je Split-Regel — beobachtet mit leicht
 * variierenden `start_date`-Werten (Seed-/Daten-Duplikate, keine echten
 * unterschiedlichen Zeitraeume), das FE renderte jedes Duplikat als eigene
 * Tabellenzeile und ließ Seiten mit 0,00-EUR-Hotspots auf 8000-10000px Höhe
 * wachsen. Dedupe daher auf (Kontakt + Prozentsatz) — das IST die Split-Regel;
 * zwei echte, unterschiedliche Regeln fuer denselben Kontakt haben immer
 * unterschiedliche Prozentsaetze.
 */
function shareDedupeKey(s: HotspotShare): string {
  return [s.contact_id, s.contact_name, s.percentage, s.fixed_amount, s.payout_currency].join('|')
}

function mapShares(raw: unknown): HotspotShare[] {
  if (!Array.isArray(raw)) return []
  const shares = raw.filter(isRecord).map((s) => ({
    id: num(s.id),
    contact_id: typeof s.contact_id === 'number' ? s.contact_id : null,
    contact_name: str(s.contact_name),
    percentage: num(s.percentage),
    split_type:
      s.split_type === 'fixed_amount' ? ('fixed_amount' as const) : ('percentage' as const),
    fixed_amount: typeof s.fixed_amount === 'number' ? s.fixed_amount : null,
    payout_currency: optStr(s.payout_currency),
    start_date: optStr(s.start_date),
    end_date: optStr(s.end_date),
    shared_hnt: num(s.shared_hnt),
    shared_iot: num(s.shared_iot),
    shared_mobile: num(s.shared_mobile),
  }))
  const seen = new Set<string>()
  return shares.filter((s) => {
    const key = shareDedupeKey(s)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function mapHotspots(raw: unknown): HotspotReward[] {
  if (!Array.isArray(raw)) return []
  return raw.filter(isRecord).map((h) => {
    const rewardsFiat = typeof h.rewards_fiat === 'number' ? h.rewards_fiat : num(h.rewards_eur)
    return {
      id: str(h.id),
      name: str(h.name),
      address: optStr(h.address),
      street: optStr(h.street),
      city: optStr(h.city),
      period_start: optStr(h.period_start),
      period_end: optStr(h.period_end),
      rewards_hnt: num(h.rewards_hnt),
      rewards_iot: num(h.rewards_iot),
      rewards_mobile: num(h.rewards_mobile),
      rewards_eur: num(h.rewards_eur) || rewardsFiat,
      rewards_fiat: rewardsFiat,
      shares: mapShares(h.shares),
    }
  })
}

function mapDaily(raw: unknown): DailyReward[] {
  if (!Array.isArray(raw)) return []
  return raw.filter(isRecord).map((d) => ({
    date: str(d.date),
    rewards_hnt: num(d.rewards_hnt),
  }))
}

function periodString(period: Record<string, unknown>): string {
  const year = num(period.year)
  const month = num(period.month)
  if (year > 0 && month > 0) {
    return `${year}-${String(month).padStart(2, '0')}`
  }
  return ''
}

/**
 * Mappt eine rohe MR-v1-Response { data: {...} } auf MonthlyReport.
 * Robust gegen fehlende Felder und gegen den total_rewards_hnt-Alias.
 */
export function mapMonthlyReport(raw: unknown): MonthlyReport {
  const root = isRecord(raw) ? raw : {}
  const data = isRecord(root.data) ? root.data : {}
  const period = isRecord(data.period) ? data.period : {}
  const summary = mapSummary(data.summary)

  return {
    period: periodString(period),
    period_label: str(period.label),
    total_hnt: summary.gross_rewards_hnt,
    total_iot: summary.rewards_iot,
    total_mobile: summary.rewards_mobile,
    summary,
    hotspots: mapHotspots(data.hotspots),
    daily: mapDaily(data.daily),
  }
}

// --- Hook ---

/**
 * R3-Review F3: `timeZone` ist hier bewusst NICHT mehr enthalten.
 * Der JSON-Report-Endpoint wertet `time_zone` gar nicht aus, das FE hat den Wert
 * aber in Query-Key und Request gehaengt — beim Mapping IANA -> Rails-Zonenname
 * ("Europe/Berlin" -> "Berlin") aenderte sich der Key und der Report wurde ein
 * zweites Mal geladen. Die Zeitzone gilt weiterhin fuer CSV-Downloads und den
 * Versand-Dialog, wo das BE sie tatsaechlich auswertet.
 */
export type UseMonthlyReportOptions = {
  contactId?: number
}

type UseMonthlyReportResult = {
  report: MonthlyReport | undefined
  isLoading: boolean
  isError: boolean
  isPremiumRequired: boolean
  isInvalidPeriod: boolean
  /**
   * R3/FE-7: aus dem additiven 402-Body ({ max_months, available_months }).
   * `max_months === 0` heisst "Free-User ausserhalb des laufenden Monats" —
   * das FE zeigt dann das v1-Upsell statt der kompletten Premium-Sperre.
   */
  maxMonths?: number
  availableMonths?: string[]
  /** True, solange ein Request laeuft — auch beim Hintergrund-Refetch. */
  isFetching: boolean
  /**
   * R3-Review F4: True, solange die angezeigten Zahlen noch aus der VORHERIGEN
   * Periode/Filterung stammen (placeholderData). Die Seite muss das sichtbar
   * machen — es sind steuerlich relevante Betraege.
   */
  isPlaceholderData: boolean
  error: unknown
  refetch: () => void
}

/** Schmales Client-Interface fuer den (untypisierten) MR-v1-Endpoint. */
type RawGetClient = {
  GET: (
    url: string,
    init: { params: { query: { month: number; year: number; contact_id?: number } } },
  ) => Promise<{ data?: unknown; error?: unknown }>
}

function statusOf(err: unknown): number | undefined {
  if (err instanceof ApiError) return err.status
  if (isRecord(err)) {
    const s = err.status ?? (isRecord(err.response) ? err.response.status : undefined)
    if (typeof s === 'number') return s
  }
  return undefined
}

/** Liest den Response-Body eines Fehlers (ApiError.body bzw. err.body). */
function bodyOf(err: unknown): Record<string, unknown> {
  if (err instanceof ApiError && isRecord(err.body)) return err.body
  if (isRecord(err) && isRecord(err.body)) return err.body
  return {}
}

/** R3/FE-7: max_months + available_months aus dem additiven 402-Body. */
function gatingInfo(err: unknown): { maxMonths?: number; availableMonths: string[] } {
  const body = bodyOf(err)
  const maxMonths = typeof body.max_months === 'number' ? body.max_months : undefined
  const availableMonths = Array.isArray(body.available_months)
    ? body.available_months.filter((m): m is string => typeof m === 'string')
    : []
  return { maxMonths, availableMonths }
}

export function useMonthlyReport(
  period: string,
  options: UseMonthlyReportOptions = {},
): UseMonthlyReportResult {
  const { contactId } = options
  const [yearStr, monthStr] = period.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)

  const client = apiClient as unknown as RawGetClient

  const query = useQuery<MonthlyReport, Error & { status?: number }>({
    queryKey: ['reports', 'monthly', period, contactId],
    queryFn: async () => {
      const result = await client.GET('/api/v2/monthly_reports', {
        params: {
          query: {
            month,
            year,
            ...(contactId !== undefined ? { contact_id: contactId } : {}),
          },
        },
      })

      if (result.error) {
        const status = statusOf(result.error)
        if (status === 402) {
          // Body (max_months/available_months) mit durchreichen — sonst kann das
          // FE den Free-Pfad (max_months 0) nicht vom echten Premium-Gate
          // unterscheiden.
          const e = new Error('subscription_required') as Error & {
            status: number
            body?: unknown
          }
          e.status = 402
          e.body = bodyOf(result.error)
          throw e
        }
        if (status === 422) {
          const e = new Error('invalid_period') as Error & { status: number }
          e.status = 422
          throw e
        }
        throw result.error
      }

      return mapMonthlyReport(result.data)
    },
    retry: false,
    // R3: Beim Wechsel von Periode/Kontakt bleibt der zuletzt geladene Report
    // sichtbar, statt die Seite auf das Skeleton zurueckzuwerfen. Die Seite
    // markiert diesen Zustand ueber isPlaceholderData sichtbar (R3-Review F4),
    // damit keine alten Zahlen unter einem neuen Monatslabel gelesen werden.
    placeholderData: (previous: MonthlyReport | undefined) => previous,
  })

  const errStatus = statusOf(query.error)
  const isPremiumRequired = errStatus === 402
  const isInvalidPeriod = errStatus === 422
  const { maxMonths, availableMonths } = gatingInfo(query.error)

  return {
    report: query.data,
    isLoading: query.isLoading,
    isError: query.isError && !isPremiumRequired && !isInvalidPeriod,
    isPremiumRequired,
    isInvalidPeriod,
    maxMonths,
    availableMonths,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error,
    refetch: () => {
      void query.refetch()
    },
  }
}
